// store/StoreProductGrid.jsx
// Category filter pills (scoped to this store's categories only) + paginated product grid.
// Fetches from /api/marketplace/products?seller=<storeSlug>
// Props: storeSlug

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Package, Tag } from "lucide-react";
import { brand } from "../shared/brand";
import { StoreProductCard } from "./StoreProductCard";

const API_URL = process.env.REACT_APP_API_URL;

export function StoreProductGrid({ storeSlug }) {
  const [products,    setProducts]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [selectedCat, setSelectedCat] = useState("");
  const [pagination,  setPagination]  = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page,        setPage]        = useState(1);

  // Fetch categories for this store once
  useEffect(() => {
    if (!storeSlug) return;
    axios
      .get(`${API_URL}/api/marketplace/categories?seller=${storeSlug}`)
      .then(res => setCategories(res.data || []))
      .catch(() => {});
  }, [storeSlug]);

  // Fetch products whenever storeSlug, category, or page changes
  useEffect(() => {
    if (!storeSlug) return;
    let cancelled = false;
    const isFirst = page === 1;

    isFirst ? setLoading(true) : setLoadingMore(true);

    const params = { seller_slug: storeSlug, per_page: 20, page };
    if (selectedCat) params.category = selectedCat;

    axios
      .get(`${API_URL}/api/marketplace/products`, { params })
      .then(res => {
        if (cancelled) return;
        const incoming = res.data.products || [];
        setProducts(prev => {
          if (isFirst) {
            const seen = new Set();
            return incoming.filter(p => seen.has(p.id) ? false : seen.add(p.id));
          }
          const existingIds = new Set(prev.map(p => p.id));
          return [...prev, ...incoming.filter(p => !existingIds.has(p.id))];
        });
        setPagination(res.data.pagination);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) { setLoading(false); setLoadingMore(false); }
      });

    return () => { cancelled = true; };
  }, [storeSlug, selectedCat, page]);

  function selectCategory(slug) {
    setSelectedCat(slug);
    setPage(1);
    setProducts([]);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="px-4">

      {/* Category pills */}
      {categories.length > 0 && (
        <div
          className="flex gap-2 overflow-x-auto pb-2 mb-5"
          style={{ scrollbarWidth: "none" }}
        >
          <CategoryPill
            label="Todos"
            isActive={selectedCat === ""}
            isTodos
            onClick={() => selectCategory("")}
          />
          {categories.map(cat => (
            <CategoryPill
              key={cat.id}
              label={cat.name}
              isActive={selectedCat === cat.slug}
              onClick={() => selectCategory(cat.slug)}
            />
          ))}
        </div>
      )}

      {/* Results count */}
      {!loading && pagination && (
        <p className="text-xs text-slate-400 mb-3">
          {pagination.total} producto{pagination.total !== 1 ? "s" : ""}
        </p>
      )}

      {/* Grid */}
      {loading ? (
        <GridSkeleton />
      ) : products.length === 0 ? (
        <EmptyState hasCatFilter={!!selectedCat} onClear={() => selectCategory("")} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {products.map(p => (
              <StoreProductCard key={p.id} product={p} storeSlug={storeSlug} />
            ))}
          </div>

          {pagination?.has_next && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={loadingMore}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {loadingMore
                  ? <><span className="size-4 rounded-full border-2 border-slate-300 border-t-slate-600 animate-spin" />Cargando...</>
                  : "Ver más"
                }
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function CategoryPill({ label, isActive, isTodos, onClick }) {
  return (
    <button
      onClick={onClick}
      className="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border transition-all"
      style={
        isActive
          ? { background: brand.primary, color: "white", borderColor: brand.primary }
          : { background: "white", color: "#374151", borderColor: "#e2e8f0" }
      }
    >
      {label}
    </button>
  );
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
          <div className="aspect-square bg-slate-100 animate-pulse" />
          <div className="p-2.5 space-y-2">
            <div className="h-3 w-full rounded bg-slate-100 animate-pulse" />
            <div className="h-3.5 w-1/3 rounded bg-slate-200 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ hasCatFilter, onClear }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <Package className="size-12 text-slate-200 mb-4" />
      <p className="text-slate-600 font-semibold mb-1">
        {hasCatFilter ? "Sin productos en esta categoría" : "Esta tienda aún no tiene productos"}
      </p>
      {hasCatFilter && (
        <button
          onClick={onClear}
          className="mt-3 rounded-xl px-4 py-2 text-sm font-semibold text-white"
          style={{ background: brand.primary }}
        >
          Ver todos
        </button>
      )}
    </div>
  );
}